import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  OnDestroy,
  AfterViewInit,
} from '@angular/core';
import { SipService } from '../services/sip.service';
import { EMPTY, Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { DtmfDialogComponent } from '../dtmf-dialog/dtmf-dialog.component';
import { EveCall } from '../models/eve-call';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  ConferenceRoom,
  ConferenceService,
} from '../services/conference.service';
import { DialogInfoService } from '../services/dialog-info.service';
import { Dialog } from '../models/dialog';
import { InviterOptions } from 'sip.js';
@Component({
  selector: 'app-dialpad',
  templateUrl: './dialpad.component.html',
  styleUrls: ['./dialpad.component.css'],
})
export class DialpadComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('remoteAudio') remoteAudio!: ElementRef<HTMLAudioElement>;

  phoneNumber: string = '';
  error: string = '';
  incomingCall: string = '';

  digits = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

  private remoteStreamSubscription?: Subscription;

  private filteredActiveCallsSubscription?: Subscription;
  private incomingCallsSubscription?: Subscription;
  private conferenceSubscription?: Subscription;

  private dialogInfoSubscription?: Subscription;

  // Active calls from SIP service (calls with session state not Terminated)
  filteredActiveCalls: EveCall[] = [];
  incomingCalls: EveCall[] = [];

  public activeDialogs: Dialog[] | null = null;
  public incomingDialogs: Dialog[] | null = null;
  public conferences: ConferenceRoom[] | null = null;
  constructor(
    private sipService: SipService,
    private dialogInfoService: DialogInfoService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private conferenceService: ConferenceService
  ) {}

  ngOnInit() {
    // ✅ Subscribe to SIP.js updates

    this.remoteStreamSubscription = this.sipService.remoteStream$.subscribe(
      (stream) => {
        if (stream) {
          this.attachAudioStream(stream);
        }
      }
    );

    // Subscribe to active calls from SIP service
    this.filteredActiveCallsSubscription =
      this.sipService.activeCalls$.subscribe((calls) => {
        this.filteredActiveCalls = calls.filter((c) => {
          return c.isInConference === false;
        });
      });

    // Subscribe to incoming calls observable.
    this.incomingCallsSubscription = this.sipService.incomingCalls$.subscribe(
      (calls) => {
        this.incomingCalls = calls;
      }
    );

    this.dialogInfoSubscription = this.sipService.dialogInfoXml$.subscribe(
      (xml: any) => {
        const dialogInfo = this.dialogInfoService.loadDialogInfo(xml);
        console.log('Loaded dialog info:', dialogInfo);
        if (dialogInfo.state === 'full') {
          this.setCompleteActiveIncomingDialogs(dialogInfo.dialogs);
        } else {
          this.setPartialActiveIncomingDialogs(dialogInfo.dialogs);
        }
      }
    );

    this.conferenceSubscription = this.conferenceService.conferences$.subscribe(
      (conferences) => {
        // Handle active conference updates
        this.conferences = conferences;
      }
    );
  }

  ngAfterViewInit() {
    console.log(
      '🎧 Remote audio element initialized:',
      this.remoteAudio.nativeElement
    );
    if (this.remoteAudio?.nativeElement) {
      const el = this.remoteAudio.nativeElement;
      el.muted = false;
      el.volume = 1;

      this.sipService.setAudioElement(el); // ✅ Pass to sipService
      this.conferenceService.bindOutput(el); // ✅ Pass to conferenceService
    }
  }

  // ✅ Bind the remote audio stream to the audio element
  attachAudioStream(stream: MediaStream) {
    if (this.remoteAudio && this.remoteAudio.nativeElement) {
      this.remoteAudio.nativeElement.srcObject = stream;
    }
  }

  async makeCall() {
    try {
      if (!this.phoneNumber) {
        console.error('Cannot make call due to errors or empty number');
        throw new Error('Invalid call parameters');
      }

      await this.holdAllUnHeldCalls(); // Hold all other unheld calls

      // find active conference and deactivate it
      const activeConference = this.conferenceService.getActiveConference();
      if (activeConference) {
        await this.conferenceService.deactivateCurrentConference();
      }

      // Initiate outgoing call via SIP service (it will add the call to the active calls list)
      const newCall = await this.sipService.makeCall(this.phoneNumber,this.phoneNumber);
      // Clear dialpad input after initiating the call
      this.phoneNumber = '';
    } catch (error: any) {
      // this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Message Content', life: 3000 });
      const errorMessage = 'Failed to make call: ' + error.message;
      this.snack.open(errorMessage, 'Dismiss');
    }
  }
  // Call control methods per active call
  async acceptCall(call: EveCall) {
    // Accept incoming call via SIP service
    try {
      await this.holdAllUnHeldCalls();

      // find active conference and deactivate it
      const activeConference = this.conferenceService.getActiveConference();
      if (activeConference) {
        await this.conferenceService.deactivateCurrentConference();
      }

      await this.sipService.acceptCall(call);

      //set status
    } catch (error: any) {
      const errorMessage = 'Failed to accept call: ' + error.message;
      this.snack.open(errorMessage, 'Dismiss');
    }
  }

  async rejectCall(call: EveCall) {
    // Reject incoming call via SIP service
    try {
      await this.sipService.rejectCall(call);
    } catch (error: any) {
      const errorMessage = 'Failed to reject call: ' + error.message;
      this.snack.open(errorMessage, 'Dismiss');
    }
  }

  async toggleHold(call: EveCall) {
    if (call.isOnHold) {
      try {
        // find active conference and deactivate it
        const activeConference = this.conferenceService.getActiveConference();
        if (activeConference) {
          await this.conferenceService.deactivateCurrentConference();
        }

        const activeCalls = this.sipService.getActiveCalls();
        // if find the call in active calls, update it to not on hold
        const unheldCall = activeCalls.find((c) => !c.isOnHold);
        if (unheldCall) {
          await unheldCall.holdCall();
        }
        await call.unholdCall();

        const updatedCalls = activeCalls.map((c) => {
          if (c === call) {
            return call;
          } else if (c === unheldCall) {
            return unheldCall;
          }
          return c;
        });
      } catch (error: any) {
        const errorMessage = 'Failed to unhold call: ' + error.message;
        this.snack.open(errorMessage, 'Dismiss');
      }
    } else {
      try {
        await call.holdCall();
      } catch (error: any) {
        const errorMessage = 'Failed to hold call: ' + error.message;
        this.snack.open(errorMessage, 'Dismiss');
      }
    }
  }

  async endCall(call: EveCall) {
    try {
      await call.endCall();
    } catch (error: any) {
      const errorMessage = 'Failed to end call: ' + error.message;
      this.snack.open(errorMessage, 'Dismiss');
    }
  }

  // dialpad.component.ts
  async mergeIncomingToConference(call: EveCall) {
    const hasActiveConference = this.conferenceService.hasActiveConference();
    try {
      // If a conference already exists, just add; else create
      await this.conferenceService.mergeCallIntoConference(call);

      const activeCalls = this.sipService.getActiveCalls();
      // Update the conference status for all active calls
      activeCalls.forEach((c) => {
        if (c === call) {
          c.isInConference = true;
        }
      });
      this.sipService.setActiveCalls(activeCalls);
    } catch (e: any) {
      const errorMessage = hasActiveConference
        ? 'Failed to merge call into conference: ' + e.message
        : 'Failed to create conference: ' + e.message;
      this.snack.open(errorMessage, 'Dismiss');
      // show a snackbar if you like
    }
  }

  debugTest() {
    // this.conferenceService.debugConferenceAudio();
    const conf = this.conferenceService.getActiveConference();
    conf?.participants.forEach((p, index) => {
      console.log(`Participant ${index}:`, {
        name: p.remoteUser,
        isMuted: p.isMuted,
        isInConference: p.isInConference,
        isOnHold: p.isOnHold,
        hasSession: !!p.session,
        sessionState: p.session?.state,
        conferenceId: (p as any).conferenceId,
      });
    });
  }
  openDtmfDialog(call: EveCall) {
    this.dialog.open(DtmfDialogComponent, {
      width: '250px',
      data: { call: call },
    });
  }

  addDigit(digit: string) {
    this.phoneNumber += digit;
  }

  deleteDigit() {
    if (this.phoneNumber.length > 0) {
      this.phoneNumber = this.phoneNumber.slice(0, -1);
    }
  }

  // async startRecording() {
  //   await this.sipService.startRecording();
  // }

  // stopRecording() {
  //   this.sipService.stopRecording();
  // }

  // async playRecording() {
  //   await this.sipService.playRecording();
  // }

  clearInput() {
    this.phoneNumber = '';
  }

  get hasCallsOrDialogsOrConferences(): boolean {
    return (
      this.filteredActiveCalls.length > 0 ||
      this.incomingCalls.length > 0 ||
      (Array.isArray(this.activeDialogs) && this.activeDialogs.length > 0) ||
      (Array.isArray(this.incomingDialogs) &&
        this.incomingDialogs.length > 0) ||
      (Array.isArray(this.conferences) && this.conferences.length > 0)
    );
  }

  ngOnDestroy() {
    this.incomingCallsSubscription?.unsubscribe();
    this.filteredActiveCallsSubscription?.unsubscribe();
    this.remoteStreamSubscription?.unsubscribe(); // ✅ Prevent memory leaks

    this.dialogInfoSubscription?.unsubscribe(); // ✅ Prevent memory leaks
    this.conferenceSubscription?.unsubscribe(); // ✅ Prevent memory leaks

    this.conferences = null;
    this.conferenceService.endConferences();
  }

  async parkCall(call: EveCall) {
    try {
      await this.sipService.transferCall(call, 'callPark@iameve.co.uk');
    } catch (error) {
      const errorMessage =
        'Failed to park call: ' +
        (error instanceof Error ? error.message : 'Unknown error');
      this.snack.open(errorMessage, 'Dismiss');
    }
  }

  setCompleteActiveIncomingDialogs(dialogs: Dialog[]) {
    // clear incomingDialogs and activeDialogs
    this.incomingDialogs = [];
    this.activeDialogs = [];

    // loop through the  dialogs and set their state
    dialogs.forEach((dialog) => {
      if (dialog.state === 'confirmed') {
        this.activeDialogs!.push(dialog);
      } else {
        this.incomingDialogs!.push(dialog);
      }
    });

    this.activeDialogs = [...this.activeDialogs!];
    this.incomingDialogs = [...this.incomingDialogs!];
  }

  setPartialActiveIncomingDialogs(dialogs: Dialog[]) {
    if (dialogs.length === 0) return;

    let hasActiveDialogsUpdated: boolean = false;
    let hasIncomingDialogsUpdated: boolean = false;

    // loop through the  dialogs and set their state
    dialogs.forEach((dialog) => {
      //check if incoming dialog
      let activeDialog = this.activeDialogs?.find(
        (d) => d.eveCallId === dialog.eveCallId
      );
      if (activeDialog) {
        if (dialog.state === 'terminated') {
          // remove from active logs lists
          this.activeDialogs = this.activeDialogs!.filter(
            (d) => d.eveCallId !== activeDialog!.eveCallId
          );
          hasActiveDialogsUpdated = true;
        } else {
          // update the dialog in incomingDialogs
          Object.assign(activeDialog, dialog);
          hasActiveDialogsUpdated = true;
        }
      } else if (dialog.state === 'confirmed') {
        this.activeDialogs!.push(dialog);
        hasActiveDialogsUpdated = true;
      }

      // find incoming dialog
      let incomingDialog = this.incomingDialogs?.find(
        (d) => d.eveCallId === dialog.eveCallId
      );

      if (incomingDialog) {
        if (dialog.state !== 'early') {
          //remove from incomingDialogs
          this.incomingDialogs = this.incomingDialogs!.filter(
            (d) => d.eveCallId !== incomingDialog!.eveCallId
          );
          hasIncomingDialogsUpdated = true;
        } else {
          // update the dialog in incomingDialogs
          Object.assign(incomingDialog, dialog);
          hasIncomingDialogsUpdated = true;
        }
      } else if (dialog.state === 'early') {
        this.incomingDialogs!.push(dialog);
        hasIncomingDialogsUpdated = true;
      }
    });

    if (hasActiveDialogsUpdated) this.activeDialogs = [...this.activeDialogs!];

    if (hasIncomingDialogsUpdated)
      this.incomingDialogs = [...this.incomingDialogs!];
  }

  async pickupDialog(dialog: Dialog) {
    let options: InviterOptions = {
      extraHeaders: [
        `Replaces: ${dialog.eveCallId};to-tag=${dialog.localTag};from-tag=${dialog.remoteTag}`,
      ],
    };

    try {
      await this.sipService.makeCall(dialog.localDisplayName??"",dialog.localUri!, options);
    } catch (error: any) {
      const errorMessage = 'Failed to pickup dialog: ' + error.message;
      this.snack.open(errorMessage, 'Dismiss');
    }
  }

  private getAllUnHoldCalls(): EveCall[] | null {
    const activeCalls = this.sipService.getActiveCalls();
    if (!activeCalls || activeCalls.length === 0) return null;
    return activeCalls.filter(
      (c) => c.isOnHold === false && c.isInConference === false
    );
  }

  private async holdAllUnHeldCalls(): Promise<void[]> {
    const unheldCalls = this.getAllUnHoldCalls();
    if (!unheldCalls || unheldCalls.length === 0) return Promise.resolve([]);
    return await Promise.all(unheldCalls.map((c) => c.holdCall()));
  }

  hasActiveConference(): boolean {
    return this.conferenceService.hasActiveConference();
  }

  hasActiveCall(): boolean {
    const activeCall = this.sipService.getActiveCalls().find((c) => {
      return !c.isOnHold && !c.isInConference;
    });
    return !!activeCall;
  }

  toggleCallMute(call: EveCall): void {
    try {
      if (call.isMuted) {
        call.unmute();
      } else {
        call.mute();
      }
    } catch (error) {
      const errorMessage =
        'Failed to toggle mute: ' +
        (error instanceof Error ? error.message : 'Unknown error');
      this.snack.open(errorMessage, 'Dismiss');
    }
  }

  private parseSipUri(uri: string): { remoteDisplayName: string | null } {
    // Regex pattern matching the C# version
    const sipUriRegex =
      /^("|)?(?<displayName>.*?)("|)?( |)?<sip(s)?:(device\.)?(?<userName>.*?)>.*$/;
    const match = uri.match(sipUriRegex);

    if (match && match.groups) {
      let displayName = match.groups['displayName']?.trim() || '';
      let userName =
        match.groups['userName']?.replace('@iameve.co.uk', '') || '';

      // If displayName is empty, use userName
      if (!displayName) {
        displayName = userName;
      }

      return { remoteDisplayName: displayName };
    }

    // Fallback: return the original URI
    return { remoteDisplayName: null };
  }
}
